import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ClientBrandingHelper,
  ClientConfigHelper,
  extractSubdomainFromHostname,
} from '@/utils';
import { ClientBrandingStorage } from '@/types/storage';
import useAuthApi from '@/api/client/useAuthApi';
import { ClientConfig } from '@goalsetter/classroom-types/auth-app';
import { brandingFailurePath } from './brandingFailurePath';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/types/redux';
import { setIcode } from '@/redux/slices/signUp';

const getExpectedClientCode = (
  icode: string | null,
  subdomain: string | null,
): string => icode || subdomain || 'goalsetter';

const getIcodeFromWindow = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  return new URLSearchParams(window.location.search).get('icode');
};

const readCacheIfItMatchesCurrentClient = (): {
  branding: ClientBrandingStorage | null;
  config: ClientConfig | null;
} => {
  const expectedClientCode = getExpectedClientCode(
    getIcodeFromWindow(),
    extractSubdomainFromHostname(),
  );
  const config = ClientConfigHelper.getClientConfig();
  const branding = ClientBrandingHelper.getClientBranding();

  if (!config?.code || config.code !== expectedClientCode) {
    ClientConfigHelper.clearClientConfig();
    ClientBrandingHelper.clearClientBranding();
    return { branding: null, config: null };
  }

  return { branding, config };
};

export interface BrandingConfigData {
  subdomain: string | null;
  branding: ClientBrandingStorage | null;
  config: ClientConfig | null;
  isLoading: boolean;
  error: string | null;
  isLoaded: boolean;
  clearBrandingConfig: () => void;
}

/**
 * Hook to load branding configuration based on subdomain.
 * Supports shared subdomain scenarios where icode (client code) parameter is provided.
 * When icode is provided, it is the client code and is used directly for lookup.
 *
 * @returns BrandingConfigData with loading state and data
 */
export const useBrandingConfig = (): BrandingConfigData => {
  const subdomain = extractSubdomainFromHostname();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { icode: icodeFromRedux } = useSelector(
    (state: RootState) => state.signUp,
  );

  // Login must follow the URL only. Redux icode is leftover from a previous
  // tenant visit (or modules logout) and would keep partner splash on /login.
  const isLoginPage = pathname === '/login';
  const icodeFromUrl = searchParams.get('icode');
  const icode = icodeFromUrl || (isLoginPage ? null : icodeFromRedux) || null;

  const loadGenerationRef = useRef(0);
  const lastEffectKeyRef = useRef<string | null>(null);

  const [branding, setBranding] = useState<ClientBrandingStorage | null>(
    () => readCacheIfItMatchesCurrentClient().branding,
  );
  const [config, setConfig] = useState<ClientConfig | null>(
    () => readCacheIfItMatchesCurrentClient().config,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(
    () => !!readCacheIfItMatchesCurrentClient().config,
  );

  const { getClientByCode, getClientBrandingBySubdomain } = useAuthApi();

  const loadBrandingConfig = useCallback(
    async (
      clientCode: string,
      subdomainForBranding: string,
      icodeParam: string | null,
    ) => {
      const generation = ++loadGenerationRef.current;
      setIsLoading(true);
      setError(null);

      try {
        const [clientConfig, clientBranding] = await Promise.all([
          getClientByCode(clientCode),
          getClientBrandingBySubdomain(
            subdomainForBranding,
            icodeParam ?? undefined,
          ),
        ]);

        // A newer request started (icode added/removed). Drop this response.
        if (generation !== loadGenerationRef.current) {
          return;
        }

        ClientConfigHelper.saveClientConfig(clientConfig);
        ClientBrandingHelper.saveClientBranding(clientBranding);
        setConfig(clientConfig);
        setBranding(clientBranding);
        setIsLoaded(true);
        setIsLoading(false);
      } catch (err) {
        if (generation !== loadGenerationRef.current) {
          return;
        }
        console.error(`Error loading branding config for ${clientCode}:`, err);
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to load branding configuration',
        );
        setIsLoading(false);
        navigate(brandingFailurePath(err), { replace: true });
      }
    },
    [navigate],
  );

  const clearBrandingConfig = useCallback(() => {
    setBranding(null);
    setConfig(null);
    setError(null);
    setIsLoaded(false);
    loadGenerationRef.current += 1;

    ClientConfigHelper.clearClientConfig();
    ClientBrandingHelper.clearClientBranding();
  }, []);

  useEffect(() => {
    if (icodeFromUrl && icodeFromUrl !== icodeFromRedux) {
      dispatch(setIcode(icodeFromUrl));
      return;
    }
    if (isLoginPage && !icodeFromUrl && icodeFromRedux) {
      dispatch(setIcode(undefined));
    }
  }, [dispatch, icodeFromUrl, icodeFromRedux, isLoginPage]);

  useEffect(() => {
    const expectedClientCode = getExpectedClientCode(icode, subdomain);
    const effectKey = `${subdomain || 'goalsetter'}-${icode || ''}`;

    const cachedConfig = ClientConfigHelper.getClientConfig();
    if (cachedConfig?.code && cachedConfig.code !== expectedClientCode) {
      ClientConfigHelper.clearClientConfig();
      ClientBrandingHelper.clearClientBranding();
      setBranding(null);
      setConfig(null);
      setIsLoaded(false);
    }

    const cachedConfigForClient = ClientConfigHelper.getClientConfig();
    const cachedBrandingForClient = ClientBrandingHelper.getClientBranding();

    if (
      !isLoginPage &&
      lastEffectKeyRef.current === effectKey &&
      cachedConfigForClient?.code === expectedClientCode &&
      cachedBrandingForClient !== null
    ) {
      return;
    }

    lastEffectKeyRef.current = effectKey;

    const subdomainForBranding = subdomain || 'goalsetter';
    loadBrandingConfig(expectedClientCode, subdomainForBranding, icode);
  }, [subdomain, icode, isLoginPage, loadBrandingConfig]);

  return {
    subdomain,
    branding,
    config,
    isLoading,
    error,
    isLoaded,
    clearBrandingConfig,
  };
};
